import { useState, useEffect, type ReactNode } from 'react'

// ── Types ──────────────────────────────────────────────────────────────────

export type ParticipantId = string

export interface Participant {
  id: ParticipantId
  label: string
  sublabel?: string
  headerBg: string
  headerBorder: string
  headerText: string
}

export interface MessageStep {
  type: 'message'
  from: ParticipantId
  to: ParticipantId
  label: string
  sublabel?: string
  isReturn?: boolean
  isAsync?: boolean
  endpoint?: { method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; url: string }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requestPayload?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  responsePayload?: any
}

export interface SelfStep {
  type: 'self'
  participant: ParticipantId
  label: string
}

export interface NoteStep {
  type: 'note'
  participant: ParticipantId
  lines: string[]
}

export interface GroupStep {
  type: 'group'
  condition: string
  steps: (MessageStep | SelfStep | NoteStep | GroupStep)[]
}

export type DiagramStep =
  | MessageStep
  | SelfStep
  | NoteStep
  | GroupStep
  | { type: 'spacer' }

export interface FlowDefinition {
  id: string
  title: string
  description?: string
  participants: Participant[]
  steps: DiagramStep[]
}

// ── Layout constants ───────────────────────────────────────────────────────

const COL = 230
const MSG_H = 38
const MSG_H_CARD = 64
const MSG_H_RETURN = 50
const SELF_H = 28
const SPACER_H = 6
const HEADER_H = 44

function cx(idx: number) {
  return idx * COL + COL / 2
}

// Compute the minimum container width needed so labels and API URLs don't overflow.
// Absolutely-positioned cards don't expand their parent, so we must pre-compute.
export function computeMinWidth(participants: Participant[], steps: DiagramStep[]): number {
  const baseW = participants.length * COL
  let minW = baseW

  function processSteps(innerSteps: DiagramStep[]) {
    for (const step of innerSteps) {
      if (step.type === 'message') {
        const fi = participants.findIndex((p) => p.id === step.from)
        const ti = participants.findIndex((p) => p.id === step.to)
        if (fi >= 0 && ti >= 0) {
          const fromX = cx(fi), toX = cx(ti)
          const midX = (fromX + toX) / 2
          const lineW = Math.abs(toX - fromX)

          // Estimate label text width (~6px per char at 11.5px font)
          const labelTextW = step.label.length * 6.5 + 24
          // Estimate sublabel width if present
          const sublabelW = step.sublabel ? step.sublabel.length * 6 + 20 : 0

          let cardNeededW = Math.max(labelTextW, sublabelW)

          if (step.endpoint && !step.isReturn) {
            // Method badge + gap + url in monospace ~6px/char
            const badgeW = step.endpoint.method.length * 6 + 16
            const urlW = step.endpoint.url.length * 6
            const endpointW = 8 + badgeW + 6 + urlW + 12
            cardNeededW = Math.max(cardNeededW, endpointW)
          }

          const lw = Math.max(lineW * 1.5, 180, cardNeededW)
          const lx = Math.max(8, midX - lw / 2)
          const needed = lx + lw + 16
          minW = Math.max(minW, needed)
        }
      }
      if (step.type === 'self') {
        const idx = participants.findIndex((p) => p.id === step.participant)
        if (idx >= 0) {
          const x = cx(idx)
          const loopW = 26
          // ~6px per char at 11px italic font
          const textW = step.label.length * 6 + 20
          const needed = x + loopW + 10 + textW + 16
          minW = Math.max(minW, needed)
        }
      }
      if (step.type === 'group') {
        processSteps((step as GroupStep).steps as DiagramStep[])
      }
    }
  }

  processSteps(steps)
  return minW
}

// ── Lifeline SVG ──────────────────────────────────────────────────────────

function Lifelines({ n, h }: { n: number; h: number }) {
  const W = n * COL
  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={W}
      height={h}
      style={{ zIndex: 0 }}
    >
      {Array.from({ length: n }, (_, i) => (
        <line
          key={i}
          x1={cx(i)}
          y1={0}
          x2={cx(i)}
          y2={h}
          stroke="#e5e7eb"
          strokeWidth={1.5}
        />
      ))}
    </svg>
  )
}

// ── Method colour ──────────────────────────────────────────────────────────

function methodColour(m: string) {
  if (m === 'GET') return '#3b82f6'
  if (m === 'POST') return '#22c55e'
  if (m === 'PUT') return '#f59e0b'
  if (m === 'PATCH') return '#8b5cf6'
  if (m === 'DELETE') return '#ef4444'
  return '#9ca3af'
}

// ── Message arrow row ──────────────────────────────────────────────────────

function MessageArrow({
  step,
  participants,
  diagramW,
  selected,
  onSelect,
}: {
  step: MessageStep
  participants: Participant[]
  diagramW: number
  selected: boolean
  onSelect: () => void
}) {
  const n = participants.length
  const W = n * COL
  const fi = participants.findIndex((p) => p.id === step.from)
  const ti = participants.findIndex((p) => p.id === step.to)
  if (fi < 0 || ti < 0) return null

  const fromX = cx(fi)
  const toX = cx(ti)
  const goRight = toX > fromX
  const lineLeft = Math.min(fromX, toX)
  const lineRight = Math.max(fromX, toX)
  const lineW = lineRight - lineLeft
  const ARROW = 7

  const isReturn = step.isReturn ?? false
  const isAsync = step.isAsync ?? false
  const isApiCall = !!step.endpoint && !isReturn
  const stroke = isReturn ? '#9ca3af' : '#4338ca'
  const dash = isReturn || isAsync ? '5,4' : undefined

  const hasDetail = !!(step.endpoint || step.requestPayload || step.responsePayload)
  const mColor = isApiCall ? methodColour(step.endpoint!.method) : undefined
  const rowH = isApiCall ? MSG_H_CARD : isReturn ? MSG_H_RETURN : MSG_H

  const midX = (fromX + toX) / 2
  const labelW = Math.max(lineW * 1.5, 180)
  const labelLeft = Math.max(8, midX - labelW / 2)

  const methodTintBg: Record<string, string> = {
    GET: 'rgba(59,130,246,0.12)',
    POST: 'rgba(34,197,94,0.13)',
    PUT: 'rgba(245,158,11,0.13)',
    DELETE: 'rgba(239,68,68,0.13)',
  }

  const cardStyle: React.CSSProperties = isApiCall
    ? {
        background: methodTintBg[step.endpoint!.method] ?? 'rgba(99,102,241,0.12)',
        borderRadius: 6,
        border: `1.5px solid ${mColor}55`,
        borderLeft: `4px solid ${mColor}`,
        padding: '3px 10px 3px 8px',
      }
    : isReturn
    ? {
        background: '#e4e4e7',
        borderRadius: 6,
        border: '1.5px solid #d4d4d8',
        padding: '3px 10px',
      }
    : {
        padding: '0 2px 2px',
      }

  return (
    <div
      className={`relative transition-colors ${hasDetail ? 'cursor-pointer' : ''} ${
        selected ? 'bg-indigo-50/50' : ''
      }`}
      style={{ height: rowH }}
      onClick={hasDetail ? onSelect : undefined}
    >
      <Lifelines n={n} h={rowH} />
      <svg
        className="absolute inset-0 pointer-events-none"
        width={W}
        height={rowH}
        style={{ zIndex: 1 }}
      >
        <line
          x1={fromX}
          y1={rowH - 10}
          x2={toX}
          y2={rowH - 10}
          stroke={stroke}
          strokeWidth={isApiCall ? 1.75 : 1.5}
          strokeDasharray={dash}
        />
        {goRight ? (
          <polygon
            points={`${toX},${rowH - 10} ${toX - ARROW - 1},${rowH - 10 - ARROW * 0.65} ${toX - ARROW - 1},${rowH - 10 + ARROW * 0.65}`}
            fill={stroke}
          />
        ) : (
          <polygon
            points={`${toX},${rowH - 10} ${toX + ARROW + 1},${rowH - 10 - ARROW * 0.65} ${toX + ARROW + 1},${rowH - 10 + ARROW * 0.65}`}
            fill={stroke}
          />
        )}
      </svg>
      <div
        className="absolute flex items-center justify-center"
        style={{ left: labelLeft, minWidth: labelW, width: 'max-content', maxWidth: diagramW - labelLeft - 8, top: 4, height: rowH - 20, zIndex: 2, pointerEvents: 'none' }}
      >
        <div
          className="flex flex-col items-center text-center w-full"
          style={{ ...cardStyle, gap: 2 }}
        >
          <div
            style={{
              fontSize: 11.5,
              fontWeight: isReturn ? 400 : 600,
              color: isReturn ? '#52525b' : '#1e1b4b',
              lineHeight: 1.3,
              maxWidth: '100%',
            }}
          >
            {step.label}
          </div>
          {step.sublabel && (
            <div style={{ fontSize: 10, color: '#6366f1', fontFamily: 'monospace' }}>
              {step.sublabel}
            </div>
          )}
          {step.endpoint && (
            <div className="flex items-center gap-1.5 justify-start">
              <span
                className="font-mono font-bold text-white shrink-0 rounded"
                style={{
                  fontSize: 9,
                  background: methodColour(step.endpoint.method),
                  padding: '1px 5px',
                  lineHeight: '15px',
                  letterSpacing: '0.04em',
                }}
              >
                {step.endpoint.method}
              </span>
              <span
                className="font-mono"
                style={{
                  fontSize: 10,
                  color: '#52525b',
                  whiteSpace: 'nowrap',
                }}
              >
                {step.endpoint.url}
              </span>
            </div>
          )}
        </div>
      </div>
      {hasDetail && (
        <div
          className="absolute right-2 text-[10px] font-medium select-none text-gray-400"
          style={{ zIndex: 2, bottom: rowH - 10 + 2 }}
        >
          ▸
        </div>
      )}
    </div>
  )
}

// ── Self-call row ──────────────────────────────────────────────────────────

function SelfCallRow({
  step,
  participants,
}: {
  step: SelfStep
  participants: Participant[]
}) {
  const n = participants.length
  const W = n * COL
  const idx = participants.findIndex((p) => p.id === step.participant)
  if (idx < 0) return null

  const x = cx(idx)
  const loopW = 26
  const loopH = 14
  const y = SELF_H / 2

  return (
    <div className="relative" style={{ height: SELF_H }}>
      <Lifelines n={n} h={SELF_H} />
      <svg
        className="absolute inset-0 pointer-events-none"
        width={W}
        height={SELF_H}
        style={{ zIndex: 1 }}
      >
        <rect
          x={x}
          y={y - loopH / 2}
          width={loopW}
          height={loopH}
          fill="none"
          stroke="#d1d5db"
          strokeWidth={1.5}
          rx={3}
        />
        <polygon
          points={`${x},${y + loopH / 2} ${x + 8},${y + loopH / 2 - 4} ${x + 8},${y + loopH / 2 + 4}`}
          fill="#d1d5db"
        />
      </svg>
      <div
        className="absolute italic"
        style={{
          left: x + loopW + 10,
          top: y - 10,
          fontSize: 11,
          color: '#6b7280',
          lineHeight: 1.3,
          zIndex: 2,
          whiteSpace: 'nowrap',
        }}
      >
        {step.label}
      </div>
    </div>
  )
}

// ── Note box ───────────────────────────────────────────────────────────────

function NoteBox({
  step,
  participants,
}: {
  step: NoteStep
  participants: Participant[]
}) {
  const n = participants.length
  const W = n * COL
  const idx = participants.findIndex((p) => p.id === step.participant)
  if (idx < 0) return null

  const x = cx(idx)
  const noteW = Math.min(COL * 1.9, W - x - 12)
  const noteH = 16 + step.lines.length * 17 + 10
  const rowH = noteH + 12

  return (
    <div className="relative" style={{ height: rowH }}>
      <Lifelines n={n} h={rowH} />
      <div
        className="absolute rounded-md border shadow-sm"
        style={{
          left: x + 12,
          top: 6,
          width: noteW,
          background: '#fefce8',
          borderColor: '#fde68a',
          padding: '6px 10px',
          zIndex: 2,
        }}
      >
        {step.lines.map((line, i) => (
          <div key={i} style={{ fontSize: 11, color: '#713f12', lineHeight: 1.55 }}>
            {line}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Group / conditional block ──────────────────────────────────────────────

function GroupBlock({
  step,
  participants,
  diagramW,
}: {
  step: GroupStep
  participants: Participant[]
  diagramW: number
}) {
  const n = participants.length

  function renderInner(s: MessageStep | SelfStep | NoteStep | GroupStep, i: number) {
    if (s.type === 'message')
      return (
        <MessageArrow
          key={i}
          step={s}
          participants={participants}
          diagramW={diagramW}
          selected={false}
          onSelect={() => {}}
        />
      )
    if (s.type === 'self') return <SelfCallRow key={i} step={s} participants={participants} />
    if (s.type === 'note') return <NoteBox key={i} step={s} participants={participants} />
    if (s.type === 'group') return <GroupBlock key={i} step={s} participants={participants} diagramW={diagramW} />
    return null
  }

  return (
    <div style={{ position: 'relative', marginTop: 2, marginBottom: 2 }}>
      <div
        style={{
          border: '2px dashed #d1d5db',
          borderRadius: 8,
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: -11,
            left: 14,
            fontSize: 11,
            fontWeight: 700,
            color: '#374151',
            background: 'white',
            padding: '2px 8px',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            zIndex: 3,
          }}
        >
          {step.condition}
        </div>
        <div style={{ position: 'relative', height: 10 }}>
          <Lifelines n={n} h={10} />
        </div>
        {step.steps.map((s, i) => renderInner(s, i))}
        <div style={{ position: 'relative', height: 4 }}>
          <Lifelines n={n} h={4} />
        </div>
      </div>
    </div>
  )
}

// ── Detail popup ──────────────────────────────────────────────────────────

function DetailPopup({
  step,
  onClose,
}: {
  step: MessageStep
  onClose: () => void
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.35)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              {step.isAsync && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                  ASYNC
                </span>
              )}
              {step.isReturn && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-200 text-gray-500">
                  RETURN
                </span>
              )}
            </div>
            <p className="text-sm font-semibold text-gray-900 leading-snug">{step.label}</p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-7 h-7 grid place-items-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors text-sm"
          >
            ✕
          </button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4">
          {step.endpoint && (
            <div className="rounded-lg bg-gray-900 text-gray-100 px-4 py-3 font-mono text-xs flex items-center gap-2 flex-wrap">
              <span className="font-bold shrink-0" style={{ color: methodColour(step.endpoint.method) }}>
                {step.endpoint.method}
              </span>
              <span className="text-gray-300 break-all">{step.endpoint.url}</span>
            </div>
          )}
          {step.requestPayload && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                Request payload
              </p>
              <pre className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-xs text-gray-800 overflow-x-auto leading-relaxed">
                {JSON.stringify(step.requestPayload, null, 2)}
              </pre>
            </div>
          )}
          {step.responsePayload && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                Response example
              </p>
              <pre className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-xs text-gray-800 overflow-x-auto leading-relaxed">
                {JSON.stringify(step.responsePayload, null, 2)}
              </pre>
            </div>
          )}
          {!step.endpoint && !step.requestPayload && !step.responsePayload && (
            <p className="text-sm text-gray-400 italic">No additional details for this step.</p>
          )}
        </div>
        <div className="shrink-0 px-5 py-2.5 border-t border-gray-100 text-[11px] text-gray-400 text-right">
          Press Esc or click outside to close
        </div>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────

export function SequenceDiagram({ flow, activeIndex = null }: { flow: FlowDefinition; activeIndex?: number | null }) {
  const [popupStep, setPopupStep] = useState<MessageStep | null>(null)
  const { participants, steps } = flow
  const n = participants.length
  const diagramW = computeMinWidth(participants, steps)

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <div style={{ minWidth: diagramW }}>
          {/* Participant headers */}
          <div
            className="grid border-b border-gray-200"
            style={{
              gridTemplateColumns: `repeat(${n}, ${COL}px)`,
              height: HEADER_H,
              background: 'linear-gradient(to bottom, #f8fafc, #fff)',
            }}
          >
            {participants.map((p, i) => (
              <div
                key={p.id}
                className={`flex flex-col items-center justify-center gap-1 px-2 ${
                  i < n - 1 ? 'border-r border-gray-100' : ''
                }`}
              >
                <div
                  className="rounded-full px-3 py-0.5 text-xs font-semibold border whitespace-nowrap"
                  style={{
                    background: p.headerBg,
                    borderColor: p.headerBorder,
                    color: p.headerText,
                  }}
                >
                  {p.label}
                </div>
                {p.sublabel && (
                  <div className="text-[10px] text-gray-400 text-center">{p.sublabel}</div>
                )}
              </div>
            ))}
          </div>

          {/* Steps */}
          <div>
            {steps.map((step, i) => {
              let content: ReactNode
              if (step.type === 'message')
                content = (
                  <MessageArrow
                    step={step}
                    participants={participants}
                    diagramW={diagramW}
                    selected={false}
                    onSelect={() => setPopupStep(step)}
                  />
                )
              else if (step.type === 'self')
                content = <SelfCallRow step={step} participants={participants} />
              else if (step.type === 'note')
                content = <NoteBox step={step} participants={participants} />
              else if (step.type === 'group')
                content = <GroupBlock step={step} participants={participants} diagramW={diagramW} />
              else if (step.type === 'spacer')
                content = <div style={{ height: SPACER_H }} />
              else
                content = null
              if (content === null) return null
              // `activeIndex` is only ever set by a live-call-driven consumer (e.g. Platform
              // Journey Tool's IDV stage) — every other caller omits it, so this wrapper is a
              // no-op div for them.
              const isActive = activeIndex != null && i === activeIndex
              return (
                <div
                  key={i}
                  style={isActive ? { background: 'rgba(99,102,241,.08)', boxShadow: 'inset 3px 0 0 #6366f1', transition: 'background .3s ease' } : undefined}
                >
                  {content}
                </div>
              )
            })}

            {/* Bottom cap */}
            <div style={{ position: 'relative', height: 10 }}>
              <Lifelines n={n} h={10} />
              {participants.map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full bg-gray-200"
                  style={{ left: cx(i) - 3, bottom: 2, width: 7, height: 7 }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {popupStep && (
        <DetailPopup step={popupStep} onClose={() => setPopupStep(null)} />
      )}
    </div>
  )
}
